import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPhoneNumber, formatPhoneNumberInput, validatePhoneNumber } from '../../utils/phoneValidation';
import { BIRTH_MONTHS, optionalZipValue, toBirthMonthValue } from '../../utils/profileFields';
import { getAdminHeaders } from '../../utils';
import { hasPermission, PERMS, validateAdminSession } from '../../utils/adminAuth';
import './ManageVolunteersPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const LIST_PREVIEW = 50;

function formatListedHours(hours?: number): string {
  const rounded = Math.round((Number(hours) || 0) * 10) / 10;
  return `${rounded}h`;
}

interface Volunteer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  zipCode: string;
  birthMonth: string;
  isMinor: boolean;
  emergencyFirstName: string;
  emergencyLastName: string;
  emergencyRelationship: string;
  emergencyPhoneNumber: string;
  passcode: string;
  createdAt: string;
  lastCheckIn?: string;
  totalHours?: number;
  isActive: boolean;
  autoCheckout: boolean;
}

interface VolunteerNote {
  id: number;
  body: string;
  createdAt: string;
  authorUsername: string;
  authorRole: string;
  authorDisplayName: string;
}

const noteRoleLabel = (role: string): string => {
  switch (role) {
    case 'supervisor':
      return 'Supervisor';
    case 'breakglass':
      return 'Break-glass Admin';
    case 'admin':
      return 'Admin';
    default:
      return role || 'Unknown';
  }
};

const formatNoteWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const noteAuthorLabel = (note: VolunteerNote): string => {
  const account = note.authorUsername || 'Unknown account';
  const name = note.authorDisplayName?.trim();
  const who = name && name.toLowerCase() !== account.toLowerCase()
    ? `${name} (${account})`
    : account;
  return `${who} · ${noteRoleLabel(note.authorRole)} · ${formatNoteWhen(note.createdAt)}`;
};

const ManageVolunteersPage: React.FC = () => {
  const navigate = useNavigate();
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [filteredVolunteers, setFilteredVolunteers] = useState<Volunteer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVolunteer, setEditingVolunteer] = useState<Volunteer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState('');

  // Form state for adding/editing volunteers
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    zipCode: '',
    birthMonth: '',
    emergencyFirstName: '',
    emergencyLastName: '',
    emergencyRelationship: '',
    emergencyPhoneNumber: ''
  });

  const [printingPasscode, setPrintingPasscode] = useState<string | null>(null);
  const [canWrite, setCanWrite] = useState(false);
  const [canPrint, setCanPrint] = useState(false);
  const [showBlankBadge, setShowBlankBadge] = useState(false);
  const [blankBadgePrinting, setBlankBadgePrinting] = useState(false);
  const [blankBadgeError, setBlankBadgeError] = useState('');
  const [notesVolunteer, setNotesVolunteer] = useState<Volunteer | null>(null);
  const [notes, setNotes] = useState<VolunteerNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  useEffect(() => {
    const init = async () => {
      const session = await validateAdminSession();
      if (!session) {
        navigate('/admin-login');
        return;
      }
      if (session.mustResetPassword) {
        navigate('/admin/change-password');
        return;
      }
      if (!hasPermission(PERMS.manageVolunteersView, session)) {
        navigate('/admin-panel');
        return;
      }
      setCanWrite(hasPermission(PERMS.manageVolunteersWrite, session));
      setCanPrint(hasPermission(PERMS.manageVolunteersPrint, session));
      fetchVolunteers();
    };
    init();
  }, [navigate]);

  useEffect(() => {
    filterVolunteers();
  }, [volunteers, searchTerm, filterStatus]);

  const fetchVolunteers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/volunteers`, {
        headers: getAdminHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setVolunteers(data);
      } else if (response.status === 401) {
        navigate('/admin-login');
      } else {
        setMessage('Failed to load volunteers');
      }
    } catch (error) {
      setMessage('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  const openNotes = async (volunteer: Volunteer) => {
    setNotesVolunteer(volunteer);
    setNotes([]);
    setNoteDraft('');
    setNotesError('');
    setNotesLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/volunteers/${volunteer.id}/notes`, {
        headers: getAdminHeaders(),
      });
      if (response.status === 401) {
        navigate('/admin-login');
        return;
      }
      if (!response.ok) {
        setNotesError('Failed to load notes');
        return;
      }
      const data = await response.json();
      setNotes(Array.isArray(data.notes) ? data.notes : []);
    } catch (error) {
      setNotesError('Failed to connect to server');
    } finally {
      setNotesLoading(false);
    }
  };

  const closeNotes = () => {
    if (noteSaving) return;
    setNotesVolunteer(null);
    setNotes([]);
    setNoteDraft('');
    setNotesError('');
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!notesVolunteer) return;
    const body = noteDraft.trim();
    if (!body) {
      setNotesError('Write a note before saving.');
      return;
    }
    setNoteSaving(true);
    setNotesError('');
    try {
      const response = await fetch(`${API_BASE_URL}/volunteers/${notesVolunteer.id}/notes`, {
        method: 'POST',
        headers: getAdminHeaders(),
        body: JSON.stringify({ body }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401) {
        navigate('/admin-login');
        return;
      }
      if (!response.ok) {
        setNotesError(data.message || 'Failed to save note');
        return;
      }
      setNotes((prev) => [data as VolunteerNote, ...prev]);
      setNoteDraft('');
    } catch (error) {
      setNotesError('Failed to connect to server');
    } finally {
      setNoteSaving(false);
    }
  };

  const filterVolunteers = () => {
    let filtered = volunteers;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(volunteer =>
        volunteer.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.passcode.includes(searchTerm)
      );
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(volunteer =>
        filterStatus === 'active' ? volunteer.isActive : !volunteer.isActive
      );
    }

    setFilteredVolunteers(filtered);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    
    // Handle phone number formatting (but not validation)
    if (name === 'phoneNumber' || name === 'emergencyPhoneNumber') {
      processedValue = formatPhoneNumberInput(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleAddVolunteer = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setFormError('');

    const phoneValidation = validatePhoneNumber(formData.phoneNumber);
    if (!phoneValidation.isValid) {
      setFormError('Phone number: ' + (phoneValidation.error || 'Invalid format'));
      return;
    }
    const emergencyPhoneValidation = validatePhoneNumber(formData.emergencyPhoneNumber);
    if (!emergencyPhoneValidation.isValid) {
      setFormError('Emergency phone number: ' + (emergencyPhoneValidation.error || 'Invalid format'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/volunteers`, {
        method: 'POST',
        headers: getAdminHeaders(),
        body: JSON.stringify({
          ...formData,
          phoneNumber: phoneValidation.formatted,
          emergencyPhoneNumber: emergencyPhoneValidation.formatted,
        }),
      });

      if (response.ok) {
        setMessage('Volunteer added successfully!');
        setShowAddForm(false);
        resetForm();
        fetchVolunteers();
      } else {
        const errorData = await response.json();
        // Show error in form since form is still visible
        setFormError(errorData.error || 'Failed to add volunteer');
      }
    } catch (error) {
      setFormError('Unable to connect to server. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditVolunteer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingVolunteer) return;

    setMessage('');
    setFormError('');

    const phoneValidation = validatePhoneNumber(formData.phoneNumber);
    if (!phoneValidation.isValid) {
      setFormError('Phone number: ' + (phoneValidation.error || 'Invalid format'));
      return;
    }
    const emergencyPhoneValidation = validatePhoneNumber(formData.emergencyPhoneNumber);
    if (!emergencyPhoneValidation.isValid) {
      setFormError('Emergency phone number: ' + (emergencyPhoneValidation.error || 'Invalid format'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/volunteers/${editingVolunteer.id}`, {
        method: 'PUT',
        headers: getAdminHeaders(),
        body: JSON.stringify({
          ...formData,
          phoneNumber: phoneValidation.formatted,
          emergencyPhoneNumber: emergencyPhoneValidation.formatted,
        }),
      });

      if (response.ok) {
        setMessage('Volunteer updated successfully!');
        setEditingVolunteer(null);
        resetForm();
        fetchVolunteers();
      } else {
        const errorData = await response.json();
        // Show error in form since form is still visible
        setFormError(errorData.error || 'Failed to update volunteer');
      }
    } catch (error) {
      setFormError('Unable to connect to server. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintBadge = async (volunteer: Volunteer) => {
    if (!window.confirm(`Print badge for ${volunteer.firstName} ${volunteer.lastName}?`)) {
      return;
    }

    setPrintingPasscode(volunteer.passcode);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/print-badge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminHeaders(),
        },
        body: JSON.stringify({
          passcode: volunteer.passcode,
          admin_override: true,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        setMessage(`Badge print submitted for ${volunteer.firstName} ${volunteer.lastName}`);
      } else if (response.status === 401) {
        navigate('/admin-login');
      } else {
        setMessage(data.message || 'Failed to print badge');
      }
    } catch (error) {
      setMessage('Failed to connect to printer service');
    } finally {
      setPrintingPasscode(null);
    }
  };

  const handlePrintBlankBadge = async () => {
    setBlankBadgePrinting(true);
    setBlankBadgeError('');
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/print-blank-badge`, {
        method: 'POST',
        headers: getAdminHeaders(),
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setMessage('Logo badge printed successfully');
        setShowBlankBadge(false);
      } else if (response.status === 401) {
        navigate('/admin-login');
      } else {
        setBlankBadgeError(data.message || 'Failed to print blank badge');
      }
    } catch {
      setBlankBadgeError('Failed to connect to printer service');
    } finally {
      setBlankBadgePrinting(false);
    }
  };

  const handleDeleteVolunteer = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this volunteer?')) return;

    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/volunteers/${id}`, {
        method: 'DELETE',
        headers: getAdminHeaders(),
      });

      if (response.ok) {
        setMessage('Volunteer deleted successfully!');
        fetchVolunteers();
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || 'Failed to delete volunteer');
      }
    } catch (error) {
      setMessage('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/volunteers/${id}/toggle-status`, {
        method: 'PUT',
        headers: getAdminHeaders(),
        body: JSON.stringify({ isActive: !currentStatus }),
      });

      if (response.ok) {
        setMessage(`Volunteer ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
        fetchVolunteers();
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || 'Failed to update volunteer status');
      }
    } catch (error) {
      setMessage('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleAutoCheckout = async (id: number, currentAutoCheckout: boolean) => {
    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/volunteers/${id}/toggle-auto-checkout`, {
        method: 'PUT',
        headers: getAdminHeaders(),
      });

      if (response.ok) {
        setMessage(`Auto-checkout ${!currentAutoCheckout ? 'enabled' : 'disabled'} successfully!`);
        fetchVolunteers();
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || 'Failed to update auto-checkout setting');
      }
    } catch (error) {
      setMessage('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (volunteer: Volunteer) => {
    setEditingVolunteer(volunteer);
    setFormData({
      firstName: volunteer.firstName,
      lastName: volunteer.lastName,
      email: volunteer.email,
      phoneNumber: formatPhoneNumber(volunteer.phoneNumber),
      zipCode: optionalZipValue(volunteer.zipCode),
      birthMonth: toBirthMonthValue(volunteer.birthMonth),
      emergencyFirstName: volunteer.emergencyFirstName,
      emergencyLastName: volunteer.emergencyLastName,
      emergencyRelationship: volunteer.emergencyRelationship,
      emergencyPhoneNumber: formatPhoneNumber(volunteer.emergencyPhoneNumber)
    });
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      zipCode: '',
      birthMonth: '',
      emergencyFirstName: '',
      emergencyLastName: '',
      emergencyRelationship: '',
      emergencyPhoneNumber: ''
    });
    setFormError('');
  };

  const handleBack = () => {
    navigate('/admin-panel');
  };

  const handleCSVImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset the input so the same file can be selected again
    e.target.value = '';

    setIsLoading(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/volunteers/import-csv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        let msg = `CSV Import Complete: ${result.imported} volunteers imported`;
        if (result.skipped > 0) {
          msg += `, ${result.skipped} skipped`;
        }
        if (result.skipped_emails && result.skipped_emails.length > 0) {
          msg += ` (${result.skipped_emails.length} duplicate emails)`;
        }
        setMessage(msg);
        fetchVolunteers();
        
        // Show detailed errors if any
        if (result.errors && result.errors.length > 0) {
          console.log('Import errors:', result.errors);
        }
      } else if (response.status === 401) {
        navigate('/admin-login');
      } else {
        const errorData = await response.json();
        setMessage(errorData.error || 'Failed to import CSV');
      }
    } catch (error) {
      setMessage('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="manage-volunteers-container">
      <div className="manage-volunteers-card">
        <div className="manage-volunteers-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="manage-volunteers-title">Manage Volunteers</h1>
          <p className="manage-volunteers-subtitle">
            Add, remove, and edit volunteer records. Manually enter or import data.
          </p>
        </div>

        <div className="manage-volunteers-content">
          {/* Controls */}
          <div className="volunteer-controls">
            <div className="search-filter-section">
              <div className="search-box">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  placeholder="Search by name, email, or user code"
                />
              </div>
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                  onClick={() => setFilterStatus('all')}
                >
                  All
                </button>
                <button
                  className={`filter-btn ${filterStatus === 'active' ? 'active' : ''}`}
                  onClick={() => setFilterStatus('active')}
                >
                  Active
                </button>
                <button
                  className={`filter-btn ${filterStatus === 'inactive' ? 'active' : ''}`}
                  onClick={() => setFilterStatus('inactive')}
                >
                  Inactive
                </button>
              </div>
            </div>
            {(canWrite || canPrint) && (
              <div className="action-buttons">
                {canWrite && (
                  <button
                    className="add-btn"
                    onClick={() => setShowAddForm(true)}
                  >
                    + Add Volunteer
                  </button>
                )}
                {canPrint && (
                  <button
                    type="button"
                    className="add-btn"
                    onClick={() => {
                      setBlankBadgeError('');
                      setShowBlankBadge(true);
                    }}
                  >
                    Print Blank Badge
                  </button>
                )}
                {canWrite && (
                  <label className="import-btn">
                    Import CSV
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCSVImport}
                      style={{ display: 'none' }}
                    />
                  </label>
                )}
              </div>
            )}
          </div>

          {/* Message - Only show success messages here */}
          {message && message.includes('successfully') && (
            <div className="message success">
              {message}
            </div>
          )}

          {canPrint && showBlankBadge && (
            <div className="volunteer-form-modal" onClick={() => !blankBadgePrinting && setShowBlankBadge(false)}>
              <div className="form-content blank-badge-modal" onClick={(e) => e.stopPropagation()}>
                <div className="blank-badge-header">
                  <h3>Print Blank Badge</h3>
                  <button
                    type="button"
                    className="modal-close"
                    onClick={() => !blankBadgePrinting && setShowBlankBadge(false)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <p className="blank-badge-hint">
                  Prints a badge with the FabMo logo only. No name or user code is printed.
                </p>
                {blankBadgeError && (
                  <div className="form-error-message">{blankBadgeError}</div>
                )}
                <div className="form-actions blank-badge-actions">
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={handlePrintBlankBadge}
                    disabled={blankBadgePrinting}
                  >
                    {blankBadgePrinting ? 'Printing...' : 'Print Badge'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add/Edit Form */}
          {canWrite && (showAddForm || editingVolunteer) && (
            <div className="volunteer-form-modal">
              <div className="form-content">
                <h3>{editingVolunteer ? 'Edit Volunteer' : 'Add New Volunteer'}</h3>
                
                {/* Form Error Message */}
                {formError && (
                  <div className="form-error-message">
                    {formError}
                  </div>
                )}
                
                <form onSubmit={editingVolunteer ? handleEditVolunteer : handleAddVolunteer}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone Number *</label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="(555)555-5555"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Zip Code (Optional)</label>
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        placeholder="Leave blank to opt out"
                      />
                    </div>
                    <div className="form-group">
                      <label>Birth Month (Optional)</label>
                      <select
                        name="birthMonth"
                        value={formData.birthMonth}
                        onChange={handleInputChange}
                      >
                        <option value="">Opt out</option>
                        {BIRTH_MONTHS.map((month) => (
                          <option key={month.value} value={month.value}>{month.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-divider">
                    <div className="divider-line"></div>
                    <span className="divider-text">Emergency Contact</span>
                    <div className="divider-line"></div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Emergency First Name *</label>
                      <input
                        type="text"
                        name="emergencyFirstName"
                        value={formData.emergencyFirstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Emergency Last Name *</label>
                      <input
                        type="text"
                        name="emergencyLastName"
                        value={formData.emergencyLastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Relationship *</label>
                      <select
                        name="emergencyRelationship"
                        value={formData.emergencyRelationship}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Relationship</option>
                        <option value="Parent">Parent</option>
                        <option value="Guardian">Guardian</option>
                        <option value="Spouse">Spouse</option>
                        <option value="Sibling">Sibling</option>
                        <option value="Friend">Friend</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Emergency Phone *</label>
                      <input
                        type="tel"
                        name="emergencyPhoneNumber"
                        value={formData.emergencyPhoneNumber}
                        onChange={handleInputChange}
                        placeholder="(555)555-5555"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="submit-btn" disabled={isLoading}>
                      {isLoading ? 'Saving...' : (editingVolunteer ? 'Update Volunteer' : 'Add Volunteer')}
                    </button>
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={() => {
                        setShowAddForm(false);
                        setEditingVolunteer(null);
                        resetForm();
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {notesVolunteer && (
            <div className="volunteer-form-modal" onClick={closeNotes}>
              <div className="form-content notes-modal" onClick={(e) => e.stopPropagation()}>
                <div className="blank-badge-header">
                  <h3>Notes for {notesVolunteer.firstName} {notesVolunteer.lastName}</h3>
                  <button
                    type="button"
                    className="modal-close"
                    onClick={closeNotes}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
                <p className="blank-badge-hint">
                  Notes are visible to admins and supervisors. Each note keeps the account that wrote it and the time it was posted.
                </p>
                {notesError && (
                  <div className="form-error-message">{notesError}</div>
                )}
                {notesLoading ? (
                  <div className="notes-empty">Loading notes...</div>
                ) : notes.length === 0 ? (
                  <div className="notes-empty">No notes yet.</div>
                ) : (
                  <div className="notes-list">
                    {notes.map((note) => (
                      <article key={note.id} className="note-card">
                        <p className="note-body">{note.body}</p>
                        <p className="note-meta">{noteAuthorLabel(note)}</p>
                      </article>
                    ))}
                  </div>
                )}
                <form className="notes-composer" onSubmit={handleAddNote}>
                  <label htmlFor="volunteer-note">Add a note</label>
                  <textarea
                    id="volunteer-note"
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    maxLength={2000}
                    rows={4}
                    placeholder="Write a note about this volunteer"
                    disabled={noteSaving || notesLoading}
                  />
                  <div className="form-actions notes-actions">
                    <button type="submit" className="notes-save" disabled={noteSaving || notesLoading}>
                      {noteSaving ? 'Saving...' : 'Add note'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Volunteers List */}
          <div className="volunteers-list">
            <h3>{searchTerm.trim() ? `Matches (${filteredVolunteers.length})` : `Volunteers (${filteredVolunteers.length})`}</h3>
            {!isLoading && !searchTerm.trim() && filteredVolunteers.length > LIST_PREVIEW && (
              <p className="volunteers-list-hint">
                Showing {LIST_PREVIEW} of {filteredVolunteers.length}. Search to find someone.
              </p>
            )}
            {isLoading ? (
              <div className="loading">Loading volunteers...</div>
            ) : filteredVolunteers.length === 0 ? (
              <div className="no-volunteers">No volunteers found</div>
            ) : (
              <div className="volunteers-table">
                <div className="table-header">
                  <div className="col-name">Name</div>
                  <div className="col-email">Email</div>
                  <div className="col-phone">Phone</div>
                  <div className="col-passcode">User code</div>
                  <div className="col-status">Status</div>
                  <div className="col-hours">Hours</div>
                  <div className="col-actions">Actions</div>
                </div>
                <div className="table-rows-container">
                  {(searchTerm.trim() ? filteredVolunteers : filteredVolunteers.slice(0, LIST_PREVIEW)).map((volunteer) => (
                    <div key={volunteer.id} className="table-row">
                    <div className="col-name">
                      {volunteer.firstName} {volunteer.lastName}
                      {volunteer.isMinor && <span className="minor-badge">Minor</span>}
                    </div>
                    <div className="col-email">{volunteer.email}</div>
                    <div className="col-phone">{volunteer.phoneNumber}</div>
                    <div className="col-passcode">{volunteer.passcode}</div>
                    <div className="col-status">
                      <span className={`status-badge ${volunteer.isActive ? 'active' : 'inactive'}`}>
                        {volunteer.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="col-hours">{formatListedHours(volunteer.totalHours)}</div>
                    <div className="col-actions">
                      <button
                        className="action-btn notes-btn"
                        onClick={() => openNotes(volunteer)}
                      >
                        Notes
                      </button>
                      {canPrint && (
                        <button
                          className="action-btn print-btn"
                          onClick={() => handlePrintBadge(volunteer)}
                          disabled={printingPasscode === volunteer.passcode}
                        >
                          {printingPasscode === volunteer.passcode ? 'Printing...' : 'Print Badge'}
                        </button>
                      )}
                      {canWrite && (
                        <>
                          <button
                            className="action-btn edit-btn"
                            onClick={() => startEdit(volunteer)}
                          >
                            Edit
                          </button>
                          <button
                            className="action-btn toggle-btn"
                            onClick={() => handleToggleStatus(volunteer.id, volunteer.isActive)}
                          >
                            {volunteer.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className={`action-btn auto-checkout-btn ${volunteer.autoCheckout ? 'auto-checkout-enabled' : 'auto-checkout-disabled'}`}
                            onClick={() => handleToggleAutoCheckout(volunteer.id, volunteer.autoCheckout)}
                          >
                            Auto Checkout
                          </button>
                          <button
                            className="action-btn delete-btn"
                            onClick={() => handleDeleteVolunteer(volunteer.id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="manage-volunteers-footer">
          <button onClick={handleBack} className="back-button">
            ← Back to Admin Panel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageVolunteersPage;
